// src/components/ModalConfirmacaoContrato.tsx
import React, { useState, useEffect } from 'react';
import { Modal } from 'react-bootstrap';
import DatePicker from 'react-datepicker';
import { ptBR } from 'date-fns/locale/pt-BR';
import 'react-datepicker/dist/react-datepicker.css';

interface ModalConfirmacaoContratoProps {
    show: boolean;
    onHide: () => void;
    onConfirm: (datas: { data_inicio: string; data_fim: string; prazo_vencimento: number }) => void;
    loading?: boolean;
}

const ModalConfirmacaoContrato: React.FC<ModalConfirmacaoContratoProps> = ({
    show,
    onHide,
    onConfirm,
    loading = false
}) => {
    const [step, setStep] = useState<1 | 2 | 3>(1);
    const [dataInicio, setDataInicio] = useState<Date | null>(new Date());
    const [dataFim, setDataFim] = useState<Date | null>(null);
    const [prazoVencimentoStr, setPrazoVencimentoStr] = useState<string>('30');
    const prazoVencimento = parseInt(prazoVencimentoStr, 10) || 0;

    // Resetar step quando modal abrir/fechar
    useEffect(() => {
        if (show) {
            setStep(1);
            const hoje = new Date();
            const daquiUmAno = new Date(hoje);
            daquiUmAno.setFullYear(daquiUmAno.getFullYear() + 1);

            setDataInicio(hoje);
            setDataFim(daquiUmAno);
            setPrazoVencimentoStr('40');
        }
    }, [show]);

    const handleConfirmar = () => {
        if (dataInicio && dataFim) {
            const dataInicioFormatada = dataInicio.toISOString().split('T')[0];
            const dataFimFormatada = dataFim.toISOString().split('T')[0];

            onConfirm({
                data_inicio: dataInicioFormatada,
                data_fim: dataFimFormatada,
                prazo_vencimento: prazoVencimento
            });
        }
    };

    // Calcular diferença em dias para exibição
    const calcularDiasVigencia = () => {
        if (dataInicio && dataFim) {
            const diffTime = Math.abs(dataFim.getTime() - dataInicio.getTime());
            const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
            return diffDays;
        }
        return 0;
    };

    const titleMap = {
        1: 'Criar Contrato Automaticamente',
        2: 'Definir Vigência do Contrato',
        3: 'Prazo de Vencimento',
    };

    return (
        <Modal show={show} onHide={onHide} centered enforceFocus={false} style={{ zIndex: 1060 }}>
            <Modal.Header closeButton>
                <Modal.Title>{titleMap[step]}</Modal.Title>
            </Modal.Header>

            {/* ETAPA 1: Informativa */}
            {step === 1 && (
                <>
                    <Modal.Body>
                        <div className="text-center">
                            <div className="avatar avatar-xl bg-primary-light rounded-circle mb-3">
                                <i className="ti ti-file-invoice fs-36 text-primary" />
                            </div>
                            <h4 className="mb-2">Oportunidade Ganha!</h4>
                            <p className="mb-0">
                                A oportunidade está na etapa do funil que gera o contrato.
                                <br />
                                Ao salvar as alterações, um novo contrato será criado automaticamente com as informações desta oportunidade.
                            </p>
                        </div>
                    </Modal.Body>
                    <Modal.Footer>
                        <button
                            type="button"
                            className="btn btn-light"
                            onClick={onHide}
                            disabled={loading}
                        >
                            Cancelar
                        </button>
                        <button
                            type="button"
                            className="btn btn-primary"
                            onClick={() => setStep(2)}
                            disabled={loading}
                        >
                            Continuar
                        </button>
                    </Modal.Footer>
                </>
            )}

            {/* ETAPA 2: Datas */}
            {step === 2 && (
                <>
                    <Modal.Body>
                        <div className="mb-4">
                            <p className="text-muted mb-3">
                                Defina a data de início e término do contrato.
                            </p>

                            <div className="row">
                                <div className="col-md-6">
                                    <div className="mb-3">
                                        <label className="col-form-label">
                                            Data de Início <span className="text-danger">*</span>
                                        </label>
                                        <div className="icon-form">
                                            <span className="form-icon">
                                                <i className="ti ti-calendar-check" />
                                            </span>
                                            <DatePicker
                                                className="form-control datetimepicker"
                                                selected={dataInicio}
                                                onChange={(date) => setDataInicio(date)}
                                                dateFormat="dd/MM/yyyy"
                                                placeholderText="DD/MM/AAAA"
                                                locale={ptBR}
                                            />
                                        </div>
                                    </div>
                                </div>

                                <div className="col-md-6">
                                    <div className="mb-3">
                                        <label className="col-form-label">
                                            Data de Término <span className="text-danger">*</span>
                                        </label>
                                        <div className="icon-form">
                                            <span className="form-icon">
                                                <i className="ti ti-calendar-check" />
                                            </span>
                                            <DatePicker
                                                className="form-control datetimepicker"
                                                selected={dataFim}
                                                onChange={(date) => setDataFim(date)}
                                                dateFormat="dd/MM/yyyy"
                                                placeholderText="DD/MM/AAAA"
                                                locale={ptBR}
                                                minDate={dataInicio || undefined}
                                            />
                                        </div>
                                    </div>
                                </div>
                            </div>

                            <div className="alert alert-info mb-0">
                                <i className="ti ti-info-circle me-2" />
                                <strong>Vigência do contrato:</strong> {calcularDiasVigencia()} dias
                            </div>
                        </div>
                    </Modal.Body>
                    <Modal.Footer>
                        <button
                            type="button"
                            className="btn btn-light"
                            onClick={() => setStep(1)}
                            disabled={loading}
                        >
                            <i className="ti ti-arrow-left me-1" />
                            Voltar
                        </button>
                        <button
                            type="button"
                            className="btn btn-primary"
                            onClick={() => setStep(3)}
                            disabled={loading || !dataInicio || !dataFim}
                        >
                            Continuar
                        </button>
                    </Modal.Footer>
                </>
            )}

            {/* ETAPA 3: Prazo de Vencimento */}
            {step === 3 && (
                <>
                    <Modal.Body>
                        <div className="mb-2">
                            <p className="text-muted mb-3">
                                Selecione o prazo para vencimento da Conta a Receber após a entrega.
                            </p>

                            {/* Valores predefinidos */}
                            <div className="d-flex flex-wrap gap-2 mb-4">
                                {[15, 28, 30, 45, 60, 90, 120].map((dias) => (
                                    <button
                                        key={dias}
                                        type="button"
                                        className={`btn ${prazoVencimento === dias ? 'btn-primary' : 'btn-outline-primary'}`}
                                        onClick={() => setPrazoVencimentoStr(String(dias))}
                                    >
                                        {dias} dias
                                    </button>
                                ))}
                            </div>

                            {/* Controle manual +/- */}
                            <label className="col-form-label d-block mb-2">Ajustar valor</label>
                            <div className="d-flex align-items-center gap-3">
                                <button
                                    type="button"
                                    className="btn btn-outline-primary"
                                    onClick={() => setPrazoVencimentoStr(String(Math.max(1, prazoVencimento - 1)))}
                                >
                                    <i className="ti-minus" />
                                </button>
                                <span className="fs-4 fw-semibold" style={{ minWidth: 80, textAlign: 'center' }}>
                                    {prazoVencimento} dias
                                </span>
                                <button
                                    type="button"
                                    className="btn btn-primary"
                                    onClick={() => setPrazoVencimentoStr(String(prazoVencimento + 1))}
                                >
                                    <i className="ti-plus" />
                                </button>
                            </div>
                        </div>
                    </Modal.Body>
                    <Modal.Footer>
                        <button
                            type="button"
                            className="btn btn-light"
                            onClick={() => setStep(2)}
                            disabled={loading}
                        >
                            <i className="ti ti-arrow-left me-1" />
                            Voltar
                        </button>
                        <button
                            type="button"
                            className="btn btn-primary"
                            onClick={handleConfirmar}
                            disabled={loading || !dataInicio || !dataFim || prazoVencimento < 1}
                        >
                            {loading ? (
                                <>
                                    <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                                    Processando...
                                </>
                            ) : (
                                <>
                                    <i className="ti ti-check me-1" />
                                    Criar Contrato
                                </>
                            )}
                        </button>
                    </Modal.Footer>
                </>
            )}
        </Modal>
    );
};

export default ModalConfirmacaoContrato;
